import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Container,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useApiGet } from '../../../utils/httpClientTs';
import {
  createFoundationDraft,
  listFoundationAvailableFunds,
  patchFoundationApplication,
  submitFoundationRecommendation,
  type FoundationAvailableFund,
  type FoundationFormAnswerInput,
  type FoundationStudentContact,
} from '../../../endpoints/foundation';
import PageHeader from '../../../Components/PageHeader';
import StudentContactFields from './StudentContactFields';
import FoundationFormRenderer from './FoundationFormRenderer';

const ACADEMIC_YEAR = '2025-2026';

const emptyContact: FoundationStudentContact = {
  student_address_1: '',
  student_address_2: '',
  student_city: '',
  student_state_id: '',
  student_postal_code: '',
  student_country_id: '',
  student_primary_phone: '',
  student_primary_phone_type: '',
  student_secondary_phone: '',
  student_secondary_phone_type: '',
};

function contactPayload(c: FoundationStudentContact) {
  return {
    student_address_1: c.student_address_1 || '',
    student_address_2: c.student_address_2 || '',
    student_city: c.student_city || '',
    student_state_id: c.student_state_id || '',
    student_postal_code: c.student_postal_code || '',
    student_country_id: c.student_country_id || '',
    student_primary_phone: c.student_primary_phone || '',
    student_primary_phone_type: c.student_primary_phone_type || '',
    student_secondary_phone: c.student_secondary_phone || '',
    student_secondary_phone_type: c.student_secondary_phone_type || '',
  };
}

export default function FoundationApplicantCreate() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [studentFirstName, setStudentFirstName] = useState('');
  const [studentLastName, setStudentLastName] = useState('');
  const [studentEmail, setStudentEmail] = useState('');
  const [contact, setContact] =
    useState<FoundationStudentContact>(emptyContact);
  const [awardedAmount, setAwardedAmount] = useState('4000');
  const [fundId, setFundId] = useState('');
  const [provisionalName, setProvisionalName] = useState('');
  const [answers, setAnswers] = useState<FoundationFormAnswerInput[]>([]);

  const { data: fundsData } = useApiGet<{ items: FoundationAvailableFund[] }>(
    ['foundation-available-funds'],
    () => listFoundationAvailableFunds({ academic_year: ACADEMIC_YEAR }),
    { enabled: true, retry: 1 }
  );
  const funds = useMemo(() => fundsData?.items ?? [], [fundsData?.items]);

  useEffect(() => {
    if (!fundId && funds.length === 1) setFundId(funds[0].id);
  }, [funds, fundId]);

  const onSubmit = async (submit: boolean) => {
    setSaving(true);
    setError(null);
    try {
      const amount = Number(awardedAmount);
      const draft = await createFoundationDraft({
        academic_year: ACADEMIC_YEAR,
        student_first_name: studentFirstName,
        student_last_name: studentLastName,
        student_email: studentEmail,
        awarded_amount: amount,
        ...contactPayload(contact),
      });
      const achAnswer = answers.find((a) => a.question_key === 'ach_required');
      await patchFoundationApplication(draft.id, {
        student_first_name: studentFirstName,
        student_last_name: studentLastName,
        student_email: studentEmail,
        awarded_amount: amount,
        ach_required: (achAnswer?.value || '').toLowerCase() === 'yes',
        fund_splits: fundId ? [{ fund_id: fundId, amount }] : [],
        answers,
        provisional_institution: provisionalName
          ? { name: provisionalName }
          : undefined,
        ...contactPayload(contact),
      });
      if (submit) {
        await submitFoundationRecommendation(draft.id);
      }
      navigate(`/foundation/applicants/${draft.id}`);
    } catch (e: any) {
      setError(e?.message || 'Failed to save recommendation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <PageHeader
        title="New Foundation recommendation"
        leftDom={undefined}
        rightDom={undefined}
        subtitle={undefined}
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Stack spacing={2}>
        <TextField
          label="Student first name"
          value={studentFirstName}
          onChange={(e) => setStudentFirstName(e.target.value)}
          required
        />
        <TextField
          label="Student last name"
          value={studentLastName}
          onChange={(e) => setStudentLastName(e.target.value)}
          required
        />
        <TextField
          label="Student email"
          value={studentEmail}
          onChange={(e) => setStudentEmail(e.target.value)}
          required
        />
        <StudentContactFields value={contact} onChange={setContact} />
        <TextField
          label="Award amount"
          type="number"
          value={awardedAmount}
          onChange={(e) => setAwardedAmount(e.target.value)}
          required
        />
        <TextField
          select
          label="Fund"
          value={fundId}
          onChange={(e) => setFundId(e.target.value)}
          helperText={
            funds.length === 0
              ? 'No available scholarship funds for your chairships'
              : undefined
          }
        >
          {funds.map((f) => (
            <MenuItem key={f.id} value={f.id}>
              {f.name} (${Number(f.available_amount).toFixed(2)} available)
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Institution (provisional name)"
          value={provisionalName}
          onChange={(e) => setProvisionalName(e.target.value)}
        />
        <FoundationFormRenderer
          formKind="recommendation"
          academicYear={ACADEMIC_YEAR}
          value={answers}
          onChange={setAnswers}
          title="Recommendation form"
        />
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            disabled={saving}
            onClick={() => onSubmit(false)}
          >
            Save draft
          </Button>
          <Button
            variant="contained"
            disabled={saving || !fundId}
            onClick={() => onSubmit(true)}
          >
            Submit recommendation
          </Button>
          <Button onClick={() => navigate('/foundation/applicants')}>
            Cancel
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Submitting creates pending fund commitments and starts review.
        </Typography>
      </Stack>
    </Container>
  );
}
